import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  initiateCloverAuth,
  integrateCloverRequest,
  integrateCloverSuccess,
  integrateCloverFailure,
} from '../slices/cloverSlice';
import ENDPOINTS from '../../api/endPoints';

// Clover OAuth redirect URL from environment variable
const CLOVER_AUTH_URL = import.meta.env.VITE_CLOVER_AUTH_URL;

interface CloverPayload {
  merchant_id: string;
  employee_id: string;
  client_id: string;
  code: string;
}

interface CloverIntegrationResponse {
  message: string;
  restaurantId: string;
  status: boolean;
}

/**
 * Handle Clover OAuth initiation - redirect to backend auth URL
 */
function* handleInitiateCloverAuth(): Generator<any, void, any> {
  try {
    // Redirect to backend authentication URL
    window.location.href = CLOVER_AUTH_URL;
  } catch (error: any) {
    console.error('Error initiating Clover auth:', error);
  }
}

/**
 * Worker saga for Clover integration
 * Handles the OAuth callback and integration process
 */
function* integrateCloverWorker(action: PayloadAction<CloverPayload>): Generator<any, void, any> {
  try {
    const { merchant_id, employee_id, client_id, code } = action.payload;

    // Store all 4 parameters in localStorage
    localStorage.setItem('clover_merchant_id', merchant_id);
    localStorage.setItem('clover_employee_id', employee_id);
    localStorage.setItem('clover_client_id', client_id);
    localStorage.setItem('clover_code', code);

    // Call API with merchant_id and code
    const url = `${ENDPOINTS.cloverIntegrate}?merchant_id=${merchant_id}&code=${code}&type=promoflow`;
    const response = yield call(axios.get, url);

    // Validate response status
    if (!response.data.status) {
      throw new Error(response.data.message || 'Integration failed');
    }

    // Extract restaurantId from response
    const restaurantId = response.data.restaurantId;

    // Validate restaurantId exists
    if (!restaurantId) {
      throw new Error('Restaurant ID not found in response');
    }

    // Store restaurantId in localStorage for use across the app
    localStorage.setItem('restaurantId', restaurantId);

    // Dispatch success action with restaurantId
    yield put(integrateCloverSuccess(restaurantId));
  } catch (error: any) {
    // Dispatch failure action
    yield put(integrateCloverFailure(error.message || 'Clover integration failed'));
  }
}

/**
 * Watcher saga for Clover integration
 */
export function* cloverSaga() {
  yield takeLatest(initiateCloverAuth.type, handleInitiateCloverAuth);
  yield takeLatest(integrateCloverRequest.type, integrateCloverWorker);
}
